import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Box, CircularProgress, Typography, Button, Container } from "@mui/material";
import { getData } from "../../Webservice/Webservice";

// Cormorant-style font from old project
const corfont = {
  fontFamily: "'Cormorant', Georgia, serif",
  letterSpacing: "0.4px",
};

// Proper responsive settings synchronized with MUI
const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 1536 }, items: 1 },
  desktop: { breakpoint: { max: 1535, min: 1200 }, items: 1 },
  laptop: { breakpoint: { max: 1199, min: 900 }, items: 1 },
  tablet: { breakpoint: { max: 899, min: 600 }, items: 1 },
  mobile: { breakpoint: { max: 599, min: 0 }, items: 1 },
};



export default function HomeBanner() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const tokenValue = localStorage.getItem("website_jwt_token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData("homepage/banner/list", null, token);

        if (response && response.banner) {
          // Filter out inactive banners and sort by position
          const activeBanners = response.banner
            .filter((banner) => banner.status === "active")
            .sort((a, b) => a.position - b.position);
          setBanners(activeBanners);
        } else {
          setError("No banners available");
        }
      } catch (err) {
        console.error("Error fetching banners:", err);
        setError("Failed to load banners. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" p={3} color="error.main">
        {error}
      </Box>
    );
  }

  if (banners.length === 0) {
    return (
      <Box textAlign="center" p={3} color="text.secondary">
        No banners available
      </Box>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 3, md: 3, lg: 4 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: "16px", sm: "24px", md: "32px" }, // Rounded corners from image
          height: { xs: "250px", sm: "350px", md: "450px", lg: "550px", xl: "600px" },
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)", // Subtle shadow for depth
        }}
      >
        <Carousel
          responsive={responsive}
          infinite
          autoPlay
          autoPlaySpeed={3000}
          transitionDuration={800}
          removeArrowOnDeviceType={["tablet", "mobile"]}
          showDots={banners.length > 1}
          pauseOnHover
          swipeable
          draggable
          customDot={<CustomDot />}
          dotListClass="custom-dot-list"
          containerClass="carousel-container"
          itemClass="carousel-item"
        >
          {banners.map((banner, index) => (
            <Box
              key={banner._id || index}
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                cursor: "pointer",
              }}
              onClick={() => {
                if (banner.externalUrl) {
                  window.open(banner.externalUrl, "_blank");
                } else if (banner.type === "product" && banner.link) {
                  navigate(`/product/${banner.link}`);
                } else if (banner.type === "subcategory" && banner.link) {
                  navigate("/search", {
                    state: {
                      subcategoryId: banner.link,
                      title: "Subcategory Products"
                    }
                  });
                }
              }}
            >
              <img
                src={banner.image}
                alt={`Banner ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/img/placeholder-banner.jpg";
                }}
                draggable={false}
              />
            </Box>
          ))}
        </Carousel>



        {/* Custom Dot Styling */}
        <style>{`
        .custom-dot-list {
          bottom: 20px !important;
          text-align: center;
        }
        .react-multi-carousel-dot button {
          width: 10px !important;
          height: 10px !important;
          border-radius: 50% !important;
          border: none !important;
          background: rgba(255, 255, 255, 0.5) !important;
          transition: all 0.3s !important;
        }
        .react-multi-carousel-dot--active button {
          background: white !important;
          transform: scale(1.2);
        }
        .carousel-item {
          padding: 0 !important;
          height: 100% !important;
        }
        .carousel-container,
        .react-multi-carousel-list,
        .react-multi-carousel-track {
          height: 100% !important;
        }
      `}</style>
      </Box>
    </Container>
  );
}

// Custom dot component
const CustomDot = ({ onClick, active }) => {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "white" : "rgba(255,255,255,0.5)",
        border: "none",
        margin: "0 5px",
        cursor: "pointer",
        transition: "all 0.3s",
        transform: active ? "scale(1.2)" : "scale(1)",
      }}
    />
  );
};
